import type { ReactNode } from 'react';
import { Icon, type IconName } from './Icon.js';

export type Tone = 'success' | 'warning' | 'danger' | 'info' | 'neutral' | 'accent';

const GLYPH: Record<Tone, IconName> = {
  success: 'check',
  warning: 'alert-triangle',
  danger: 'alert-circle',
  info: 'info',
  neutral: 'dot',
  accent: 'dot',
};

export interface StatusPillProps {
  tone: Tone;
  children: ReactNode;
  /** Override the tone's default glyph; status is never colour alone (01-foundations §7.3). */
  icon?: IconName;
  className?: string;
  title?: string;
}

/** A state word with a glyph and a tinted background (03-components §9). Not interactive. */
export function StatusPill({ tone, children, icon, className, title }: StatusPillProps) {
  return (
    <span className={['pill', `pill--${tone}`, className].filter(Boolean).join(' ')} title={title}>
      <Icon name={icon ?? GLYPH[tone]} size={12} />
      <span className="pill__label">{children}</span>
    </span>
  );
}

export interface BadgeProps {
  /** Rendered through the 99+ cap by the caller (formatBadgeCount) or given as text. */
  children: ReactNode;
  tone?: Extract<Tone, 'neutral' | 'accent' | 'danger'>;
  className?: string;
  /** Announced label when the number alone is ambiguous ("3 open"). */
  label?: string;
}

/** A count pinned to a label or icon (03-components §9). */
export function Badge({ children, tone = 'neutral', className, label }: BadgeProps) {
  return (
    <span className={['badge', `badge--${tone}`, className].filter(Boolean).join(' ')} aria-label={label}>
      <span className="badge__label">{children}</span>
    </span>
  );
}
